package com.crowpay.views.screens.common

import com.crowpay.CONTRACTOR_REQUIRED
import com.crowpay.FOR_CONTRACTORS
import com.crowpay.FOR_HOMEOWNERS
import com.crowpay.HOMEOWNER_REQUIRED
import com.crowpay.REQUIRED
import com.crowpay.SharedEvents
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.bell
import com.crowpay.envelope
import com.crowpay.lock
import com.crowpay.messageBubble
import com.crowpay.notificationBell
import com.crowpay.notifications.*
import com.crowpay.padlock
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.speechBubble
import com.crowpay.utils.Action
import com.crowpay.utils.containsAny
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.pushChanges
import com.crowpay.utils.resize
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.components.space
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.BackgroundSemantic
import com.crowpay.views.theming.ForegroundSemantic
import com.crowpay.views.theming.PurpleSelectButtons
import com.crowpay.views.theming.SetCornerRadii
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.colored
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.subTitle
import com.crowpay.views.theming.title
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.CornerRadii
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.SelectedSemantic
import com.lightningkite.kiteui.models.UnselectedSemantic
import com.lightningkite.kiteui.models.div
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.models.times
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atBottom
import com.lightningkite.kiteui.views.atBottomEnd
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.bold
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.direct.Switch
import com.lightningkite.kiteui.views.direct.ToggleButton
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.hintPopover
import com.lightningkite.kiteui.views.direct.icon
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.switch
import com.lightningkite.kiteui.views.direct.toggleButton
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.kiteui.views.selected
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq
import com.lightningkite.serialization.lensPath

sealed interface SubscriptionSetting {
    val name: String
    val required: Boolean

    val email: Writable<Boolean>
    val sms: Writable<Boolean>
    val push: Writable<Boolean>

    val frequency: Writable<NotificationFrequency>

    val unsavedChanges: Readable<Boolean>

    suspend fun save()
    fun discard()

    class Base(
        val eventInfo: SharedEvents.Event,
        val subscriptions: Readable<Map<UserEventType<UUID>, EventSubscription<UUID>>>,
        lens: ProjectLens
    ) : SubscriptionSetting {
        override val name: String get() = eventInfo.displayName

        override val required = eventInfo.tags.containsAny(
            if (lens.isClient()) setOf(EventSubscription.REQUIRED, EventSubscription.HOMEOWNER_REQUIRED)
            else setOf(EventSubscription.REQUIRED, EventSubscription.CONTRACTOR_REQUIRED)
        )

        val subscription = Draft {
            val session = notNullSession()
            subscriptions()[UserEventType(session.userId, eventInfo.type)]
                ?.also { if (eventInfo == SharedEvents.projectMessageSent) println(it) }
                ?: notFoundError<EventSubscription<UUID>>("No subscription found for ${eventInfo.name}")
        }

        override val email: Writable<Boolean> =
            if (required) Constant(true).withWrite { /*NOOP*/ } else subscription.lensPath { it.email }
        override val sms: Writable<Boolean> = subscription.lensPath { it.sms }
        override val push: Writable<Boolean> = subscription.lensPath { it.push }

        override val frequency: Writable<NotificationFrequency> = subscription.lensPath { it.frequency }

        override suspend fun save() {
            subscription.pushChanges(notNullSession().subscriptions)
        }

        override fun discard() = subscription.cancel()

        override val unsavedChanges: Readable<Boolean> get() = subscription.changesMade
    }

    class Group(override val name: String, val settings: List<SubscriptionSetting>) : SubscriptionSetting {
        init {
            require(settings.isNotEmpty()) { "Settings must not be empty for group $name" }
        }

        constructor(
            name: String,
            events: List<SharedEvents.Event>,
            subscriptions: Readable<Map<UserEventType<UUID>, EventSubscription<UUID>>>,
            lens: ProjectLens
        ) : this(name, events.map { Base(it, subscriptions, lens) })

        override val required: Boolean = settings.any { it.required }

        private fun setting(getSetting: (SubscriptionSetting) -> Writable<Boolean>): Writable<Boolean> =
            shared {
                settings.filter { !it.required }.all { getSetting(it).invoke() }
            }.withWrite { value ->
                settings.forEach { getSetting(it).set(value) }
            }

        override val email: Writable<Boolean> = setting { it.email }
        override val sms: Writable<Boolean> = setting { it.sms }
        override val push: Writable<Boolean> = setting { it.push }

        override val frequency: Writable<NotificationFrequency> = shared {
            settings.first().frequency()
        }.withWrite { value ->
            settings.forEach { it.frequency.set(value) }
        }

        override suspend fun save() {
            settings.forEach { it.save() }
        }

        override fun discard() {
            settings.forEach { it.discard() }
        }

        override val unsavedChanges: Readable<Boolean> = shared { settings.any { it.unsavedChanges() } }
    }
}

class NotificationSubscriptionSettings(val lens: ProjectLens) : Page {
    val subscriptions = shared {
        val session = notNullSession()
        session.subscriptions.query(
            Query(
                condition { it._id.user eq session.userId },
                limit = Int.MAX_VALUE
            )
        )().associateBy { it._id }
    }

    private fun settingsOf(vararg events: SharedEvents.SharedEvent) = events
        .flatMap {
            when (it) {
                is SharedEvents.Event -> listOf(it)
                is SharedEvents.WithReminders -> listOf(it.event, it.reminders)
            }
        }
        .filter {
            if (lens.isClient()) FOR_CONTRACTORS !in it.tags
            else FOR_HOMEOWNERS !in it.tags
        }
        .map {
            SubscriptionSetting.Base(it, subscriptions, lens)
        }

    private fun groupOf(name: String, vararg events: SharedEvents.SharedEvent): List<SubscriptionSetting.Group>? {
        val settings = settingsOf(*events)
        return if (settings.isEmpty()) null
        else listOf(SubscriptionSetting.Group(name, settings))
    }

    private val projectAlerts = SubscriptionSetting.Group(
        "General Project Alerts",
        SharedEvents.run {
            settingsOf(
                projectStatusUpdated,
                projectMessageSent,
                projectPublished,
                projectAccepted,
                canStartProject
            )
        }
    )

    private val workItemAlerts = SubscriptionSetting.Group(
        "Work Item Alerts",
        SharedEvents.run {
            settingsOf(
                lineItemStatusUpdated,
                newNoteActivity,
                newTaskActivity
            )
        }
    )

    private val bankAndAccountSetupAlerts = SubscriptionSetting.Group(
        "Bank and Account Setup",
        SharedEvents.run {
            settingsOf(
                homeownerConnectBankAccount,
                contractorConnectBankAccount
            )
        }
    )

    private val escrowAlerts = SubscriptionSetting.Group(
        "Escrow Alerts",
        SharedEvents.run {
            settingsOf(
                escrowDepositRequested,
                escrowFundsReceived,
                escrowFullyFunded
            )
        }
    )

    private val drawAlerts = SubscriptionSetting.Group(
        "Draws",
        SharedEvents.run {
            settingsOf(
                drawReady,
                drawItemsApproved,
                drawItemsWaited,
                drawFullyApproved
            )
        }
    )

    private val changeRequestAlerts = SubscriptionSetting.Group(
        "Change Alerts",
        SharedEvents.run {
            listOfNotNull(
                settingsOf(changeRequestPublished),
                groupOf(
                    "Change Item Approved",
                    pendingLineItemApproved,
                    pendingItemChangeApproved
                ),
                groupOf(
                    "Change Item Rejected",
                    pendingLineItemRejected,
                    pendingItemChangeRejected
                ),
                settingsOf(
                    changeFullyApproved,
                    changeFullyRejected
                )
            ).flatten()
        }
    )

    private val paymentAlerts = SubscriptionSetting.Group(
        "Payments",
        SharedEvents.run {
            settingsOf(
                lineItemSignOffRequested,
                lineItemAutoCompleted,
                fundsReleasedPending,
                fundsReleasedConfirmed
            )
        }
    )

    private val groups = listOf(
        projectAlerts,
        workItemAlerts,
        bankAndAccountSetupAlerts,
        escrowAlerts,
        drawAlerts,
        changeRequestAlerts,
        paymentAlerts
    )

    private fun ViewWriter.renderSettingsControls(group: SubscriptionSetting.Group): ViewModifiable {
        return col {
            row {
                expanding - atBottom - bold - subTitle(group.name)

                row {
                    if (group.required) {
                        buttonTheme - hintPopover { body("Send notifications via email") } - button {
                            dynamicTheme {
                                if (group.email()) SelectedSemantic else UnselectedSemantic
                            }
                            icon(Icon.email.resize(1.5.rem), "email")
                            onClick {
                                val enabled = group.email()
                                group.settings.forEach { it.email set !enabled }
                            }
                        }
                    } else {
                        centered - buttonTheme - hintPopover { body("Send notifications via email") } - toggleButton {
                            icon(Icon.email.resize(1.5.rem), "email")
                            checked bind group.email
                        }
                    }
                    centered - buttonTheme - hintPopover { body("Send notifications via sms") } - toggleButton {
                        icon(Icon.messageBubble.resize(1.5.rem), "sms")
                        checked bind group.sms
                    }
                    centered - buttonTheme - hintPopover { body("Send notifications via push notifications") } - toggleButton {
                        icon(Icon.notificationBell.resize(1.5.rem), "push notifications")
                        checked bind group.push
                    }
                }
            }

            greySeparator()

            col {
                for (setting in group.settings) {
                    row {
                        if (viewIsWide) space()

                        centered - if (setting.required) {
                            expanding - row {
                                spacing = 0.1.rem
                                body(setting.name)
                                colored(AppColors.red.main) - bodyBold("*")
                            }
                        } else expanding - body(setting.name)

                        row {
                            fun roundToggle(setup: ToggleButton.() -> Unit) = buttonTheme - stack {
                                themeChoice += SetCornerRadii { CornerRadii.ForceConstant(1.5.rem / 2) }
                                forcePadding = true
                                centered - sizeConstraints(width = 1.5.rem, height = 1.5.rem) - toggleButton(setup)
                            }

                            fun customToggle(setup: ToggleButton.() -> Unit) = buttonTheme - padded - stack {
                                centered - sizeConstraints(width = 1.5.rem, height = 1.5.rem) - buttonTheme - toggleButton(setup)
                            }

                            if (setting.required) {
                                buttonTheme - padded - stack {
                                    centered - colored(AppColors.purple.dark) - icon {
                                        source = Icon.lock.resize(1.5.rem)
                                        description = "required"
                                    }
                                }
//                                buttonTheme - stack {
//                                    themeChoice += SetCornerRadii { CornerRadii.ForceConstant(1.5.rem / 2) }
//                                    forcePadding = true
//                                    centered - selected - sizeConstraints(width = 1.5.rem, height = 1.5.rem) - stack {
//                                        spacing = 0.px
//                                        centered - icon(Icon.lock.resize(1.2.rem), "required")
//                                    }
//                                }
                            } else {
                                customToggle {
                                    checked bind setting.email
                                }
                            }
                            customToggle {
                                checked bind setting.sms
                            }
                            customToggle {
                                checked bind setting.push
                            }
                        }
                    }
                }
            }
        }
    }

    private fun ViewWriter.renderSettingsControls2(group: SubscriptionSetting.Group): ViewModifiable {
        return col {
            row {
                expanding - atBottom - bold - subTitle(group.name)

                col {
                    centered - ForegroundSemantic(AppColors.primary.main).onNext - icon(
                        Icon.envelope.resize(1.5.rem),
                        "email"
                    )
                    BackgroundSemantic(AppColors.secondary.main).onNext - sizeConstraints(width = 3.rem) - switch {
//                        enabled = !group.required
                        checked bind group.email
                    }
                }

                col {
                    centered - ForegroundSemantic(AppColors.primary.main).onNext - icon(
                        Icon.speechBubble.resize(1.5.rem),
                        "sms"
                    )
                    BackgroundSemantic(AppColors.secondary.main).onNext - sizeConstraints(width = 3.rem) - switch {
                        checked bind group.sms
                    }
                }

                col {
                    centered - ForegroundSemantic(AppColors.primary.main).onNext - icon(
                        Icon.bell.resize(1.5.rem),
                        "push"
                    )
                    BackgroundSemantic(AppColors.secondary.main).onNext - sizeConstraints(width = 3.rem) - switch {
                        checked bind group.push
                    }
                }
            }

            greySeparator()

            col {
                for (setting in group.settings) {
                    row {
                        if (viewIsWide) space()

                        centered - if (setting.required) {
                            expanding - row {
                                spacing = 0.1.rem
                                body(setting.name)
                                colored(AppColors.red.main) - bodyBold("*")
                            }
                        } else expanding - body(setting.name)

                        row {
                            fun customToggle(setup: Switch.() -> Unit) =
                                sizeConstraints(width = 3.rem) - BackgroundSemantic(AppColors.secondary.main).onNext - switch(
                                    setup
                                )

                            if (setting.required) {
                                centered - sizeConstraints(width = 2.5.rem) - ForegroundSemantic(AppColors.primary.main).onNext - icon(
                                    Icon.padlock,
                                    "required"
                                )
                            } else {
                                customToggle {
                                    checked bind setting.email
                                }
                            }
                            customToggle {
                                checked bind setting.sms
                            }
                            customToggle {
                                checked bind setting.push
                            }
                        }
                    }
                }
            }
        }
    }

    fun renderSettings(writer: ViewWriter) = with(writer) {
        PurpleSelectButtons.onNext - sizeConstraints(width = AppDimensions.pageWidth) - col {
            spacing = AppDimensions.majorColumnSpacing * 2
            for (group in groups) {
                if (viewIsNarrow) renderSettingsControls(group)
                else renderSettingsControls2(group)
            }
        }
    }

    val discard = Action("Discard changes") {
        groups.filter { it.unsavedChanges() }.forEach { it.discard() }
    }

    val save = Action("Save changes") {
        groups.filter { it.unsavedChanges() }.forEach { it.save() }
    }

    fun saveControls(writer: ViewWriter) = with(writer) {
        row {
            existsDefaultFalse { groups.any { it.unsavedChanges() } }
            secondaryButton - button {
                specCenteredText("Discard")
                action = discard
            }
            primaryButton - button {
                specCenteredText("Save Changes")
                action = save
            }
        }
    }

    override fun ViewWriter.render2(): ViewModifiable {
        return stack {
            scrolls - col {
                col {
                    spacing = AppDimensions.majorColumnSpacing
                    title("Notification Settings")

                    renderSettings(this)
                }

                space(2.0)
            }
            atBottomEnd - stack {
                saveControls(this)
            }
        }
    }
}

@Routable("/client/notifications")
class ClientNotificationSettings() : Page by NotificationSubscriptionSettings(ProjectLens.Customer)

@Routable("/contractor/notifications")
class ContractorNotificationSettings() : Page by NotificationSubscriptionSettings(ProjectLens.Contractor)